/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import Editor from '@ckeditor/ckeditor5-core/src/editor';
import { ConnectedRouter } from 'connected-react-router';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import styled, { ThemeProvider } from 'styled-components';
import { RichTextEditor } from 'components/atoms/rich-text-editor';
import { Tag } from 'components/atoms/tag';
import { ALL_COLORS } from 'components/colors';
import { FilterCriteriaAsText } from 'components/organisms/dashboard/filter-as-text';
import { FieldType } from 'components/organisms/tasks/fields';
import { FieldFilter } from 'components/organisms/tasks/filters';
import { theme } from 'components/styles/theme';
import { store, history } from 'data';
import { TemplateObjectType } from 'data/templates/api';
import { useActiveDocumentTemplateId } from 'data/templates/documents/hooks/use-active-document-id';
import { useDocumentById } from 'data/templates/documents/hooks/use-document-by-id';
import { useActiveProjectTemplate } from 'data/templates/projects';
import { useActiveStatusUpdateTemplateId } from 'data/templates/status-updates/hooks/use-active-status-update';
import { getTemplatesDetailsQueryKey, queryClient } from 'queries';
import translations from 'translations/en.json';
import { ConditionalVariables } from './conditional-variables';

const Wrapper = styled.div`
  background-color: var(--coolest-gray-10);
  padding: var(--spacing-05);
  padding-left: var(--spacing-04);
  cursor: pointer;
  border-radius: var(--spacing-03);
`;

const EditorWrapper = styled.div`
  margin-left: calc(var(--spacing-03) - 2px);
`;

const VariableFilterCriteriaAsText = ({ criterion }) => {
  const {
    field: { type },
  } = criterion;

  /**
   * since filter transformers aren't used as we are not sending
   * the filter data over API
   * for date filter type, we are transforming the value
   * since it expects date value in moment format.
   */
  if (type === FieldType.Date) {
    return (
      <FilterCriteriaAsText
        criterion={{
          ...criterion,
          ...(criterion.value
            ? {
                value: moment(criterion.value),
              }
            : {}),
        }}
      />
    );
  }

  return <FilterCriteriaAsText criterion={criterion} />;
};

const formatVariable = (variable) => {
  return {
    ...variable,
    ifBlock: {
      ...variable.ifBlock,
      condition: {
        ...variable.ifBlock.condition,
        ...(variable.ifBlock.condition.value &&
        variable.ifBlock.condition.field.type === FieldType.Date
          ? {
              value: moment(variable.ifBlock.condition.value),
            }
          : {}),
      },
    },
    elseIfBlocks: variable.elseIfBlocks.map((_block) => ({
      ..._block,
      condition: {
        ..._block.condition,
        ...(_block.condition.value &&
        _block.condition.field.type === FieldType.Date
          ? {
              value: moment(_block.condition.value),
            }
          : {}),
      },
    })),
  };
};

const ConditionalVariableText = ({
  variableId,
  templateType,
  editor,
}: {
  variableId: any;
  templateType?: TemplateObjectType;
  editor: Editor | null;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const projectTemplate = useActiveProjectTemplate();
  const activeDocTemplateId = useActiveDocumentTemplateId();
  const activeDocumentTemplate = useDocumentById(activeDocTemplateId);
  const activeStatusUpdateTemplateId = useActiveStatusUpdateTemplateId();

  const conditionalVariableData: any = useMemo(() => {
    switch (templateType) {
      case TemplateObjectType.Project:
        return projectTemplate.templateMeta?.conditionalVariables?.[variableId];

      case TemplateObjectType.Document:
        return activeDocumentTemplate?.templateMeta?.conditionalVariables?.[
          variableId
        ];

      case TemplateObjectType.StatusUpdates:
        return queryClient.getQueryData<any>(
          getTemplatesDetailsQueryKey(activeStatusUpdateTemplateId),
        )?.templateMeta?.conditionalVariables?.[variableId];

      default:
        return null;
    }
  }, [
    templateType,
    projectTemplate,
    variableId,
    activeDocumentTemplate,
    activeStatusUpdateTemplateId,
  ]);

  if (!conditionalVariableData) {
    return <></>;
  }

  const { formValues: _conditionalVariable } = conditionalVariableData;

  const _variable = _conditionalVariable ?? conditionalVariableData;

  const conditionalVariable = formatVariable(_variable);

  return (
    <>
      <Wrapper
        onClick={() => {
          setIsEditing(true);
        }}
      >
        {FieldFilter.isValid(conditionalVariable.ifBlock.condition) &&
        conditionalVariable.ifBlock.value ? (
          <>
            <div>
              <Tag color={ALL_COLORS[0]}>
                <b>{'If '}</b>
                &nbsp;
                <VariableFilterCriteriaAsText
                  criterion={conditionalVariable.ifBlock.condition}
                />
              </Tag>
              <EditorWrapper>
                <RichTextEditor
                  readonly
                  data={conditionalVariable.ifBlock.value ?? ''}
                />
              </EditorWrapper>
            </div>
            {conditionalVariable.elseIfBlocks?.map((_block) => {
              const { condition, value } = _block;

              return (
                <>
                  {FieldFilter.isValid(condition) && value ? (
                    <>
                      <Tag color={ALL_COLORS[0]}>
                        <b>{'else if '}</b>
                        &nbsp;
                        <VariableFilterCriteriaAsText criterion={condition} />
                      </Tag>
                      <EditorWrapper>
                        <RichTextEditor readonly data={value ?? ''} />
                      </EditorWrapper>
                    </>
                  ) : null}
                </>
              );
            })}
            <div>
              <Tag color={ALL_COLORS[0]}>
                <b>{'Else '}</b>&nbsp;
              </Tag>
              <EditorWrapper>
                <RichTextEditor
                  readonly
                  data={conditionalVariable.elseBlock ?? ''}
                />
              </EditorWrapper>
            </div>
          </>
        ) : null}
      </Wrapper>
      {isEditing ? (
        <ConditionalVariables
          variableId={variableId}
          templateType={templateType}
          formValues={conditionalVariable}
          onClose={() => setIsEditing(false)}
          {...(editor !== null ? { editor } : {})}
        />
      ) : null}
    </>
  );
};

export const ConditionalVariablesAsText = ({
  variableId,
  templateType,
  editor,
}: {
  variableId: any;
  templateType?: TemplateObjectType;
  editor: Editor | null;
}) => {
  return (
    <Provider store={store}>
      <ConnectedRouter history={history}>
        <ThemeProvider theme={theme}>
          <IntlProvider locale="en" messages={translations}>
            <QueryClientProvider client={queryClient}>
              <ConditionalVariableText
                variableId={variableId}
                templateType={templateType}
                editor={editor}
              />
            </QueryClientProvider>
          </IntlProvider>
        </ThemeProvider>
      </ConnectedRouter>
    </Provider>
  );
};
